import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fadeUp } from "@/lib/motion";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Send the current window origin so the backend can build a reset link that
      // points back to exactly the site the user is currently on (preview vs
      // production vs custom domain). This is the most reliable signal — Referer
      // and X-Forwarded-Host can be stripped or rewritten by proxies.
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      await api.post("/auth/forgot-password", { email, origin });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not send reset email."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6">
      <Toaster position="top-center" />
      <motion.div
        {...fadeUp}
        data-testid="forgot-page"
        className="w-full max-w-md bg-white border border-[#E5E5E5] p-10 md:p-14"
      >
        <Link to="/" className="font-serif text-2xl text-[#1A1F24]">
          Gemma <span className="italic text-[#C5A059]">Veda</span>
        </Link>
        <h1 className="font-serif text-3xl mt-6 text-[#1A1F24]">Reset password</h1>

        {!sent ? (
          <>
            <p className="mt-2 text-sm text-[#5C6670] leading-relaxed">
              Enter your studio email. We&apos;ll send a one-time link to set a new password.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-6" data-testid="forgot-form">
              <div>
                <label className="gv-label">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="gemmavedaofficial@gmail.com"
                  className="gv-input mt-2"
                  data-testid="forgot-email"
                />
              </div>
              <button type="submit" disabled={submitting} className="gv-btn-gold w-full justify-center" data-testid="forgot-submit">
                {submitting ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-sent" className="mt-6">
            <div className="border-l-2 border-[#C5A059] pl-4 py-3 bg-[#FAFAFA]">
              <p className="text-sm text-[#1A1F24] leading-relaxed">
                If <strong>{email}</strong> matches a studio account, a reset link is on its way.
                Check your inbox (and Spam folder, just in case) within a few minutes.
              </p>
            </div>
            <p className="mt-6 text-xs text-[#5C6670]">The link expires in <strong>1 hour</strong> and can only be used once.</p>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <Link to="/admin/login" className="text-xs tracking-[0.22em] uppercase text-[#5C6670] hover:text-[#C5A059]" data-testid="forgot-back-login">
            ← Back to sign in
          </Link>
          <Link to="/" className="text-xs tracking-[0.22em] uppercase text-[#5C6670] hover:text-[#C5A059]">
            Site home
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
